import {
  type DataGridProps,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridFilterModel,
  type GridPaginationModel,
  type GridSortModel,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { type QueryKey } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApiErrorMessage } from '@/shared/data/errors';
import { useInvalidateQueries } from '@/shared/hooks';
import {
  computeStorageKey,
  getPersistedState,
  parseUrlState,
  type GridColumnWidthModel,
} from './gridState';
import { type GridQueryInput, type GridQueryResult, mapGridFilters } from './gridUtils';
import { useGridCallbacks } from './useGridCallbacks';
import { useGridInitialState } from './useGridInitialState';
import { useGridPersistence } from './useGridPersistence';
import { useGridQueryInput } from './useGridQueryInput';
import { useGridQueryKey } from './useGridQueryKey';
import { useGridQueryLifecycle } from './useGridQueryLifecycle';
import { useGridScrollRetention } from './useGridScrollRetention';
import { useGridSortState } from './useGridSortState';
import { useGridUrlSync } from './useGridUrlSync';

type UseServerDataGridStateParams<T extends GridValidRowModel> = {
  queryPath: string;
  queryFn: (input: GridQueryInput) => Promise<GridQueryResult<T>>;
  initialData?: GridQueryResult<T>;
  columns: ReadonlyArray<GridColDef<T>>;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  height?: number;
  noRowsText?: string;
  filteredNoRowsText?: string;
  onRowsChange?: (rows: T[]) => void;
  onQueryMeta?: (meta: { total: number; items: T[] } | null) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  fixedFilters?: GridQueryInput['filters'];
  search?: string;
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  queryKeyPrefix?: QueryKey;
  queryKey?: QueryKey;
  restoreFilterModel?: boolean;
  onRefetch?: (refetch: () => Promise<void>) => void;
  onInvalidate?: (invalidate: () => Promise<void>) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  isAuthenticated?: boolean;
  organisationId?: string | null;
  projectId?: string | null;
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  autoHeight?: boolean;
  columnVisibilityModel?: GridColumnVisibilityModel;
  columnVisibilityPresets?: Record<string, GridColumnVisibilityModel>;
  columnVisibilityPreset?: string;
  refetchIntervalMs?: number | false;
  staleTimeMs?: number;
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean | 'always';
  skeletonGetRowHeight?: DataGridProps['getRowHeight'] | null;
  getRowHeight?: DataGridProps['getRowHeight'];
};

export type UseServerDataGridStateResult<T extends GridValidRowModel> = {
  errorMessage: string | null;
  rowsData: T[];
  rowCount: number;
  showInitialSkeleton: boolean;
  showFetchSkeleton: boolean;
  showPaginationSkeleton: boolean;
  lastUpdatedAt: number | null;
  queryIsFetching: boolean;
  queryIsFetched: boolean;
  resolvedPageSizeOptions: number[];
  resolvedInitialPageSize: number;
  resolvedHeight: number | undefined;
  resolvedNoRowsText: string;
  resolvedSkeletonRowHeight: DataGridProps['getRowHeight'] | undefined;
  paginationModel: GridPaginationModel;
  setPaginationModel: Dispatch<SetStateAction<GridPaginationModel>>;
  handlePaginationModelChange: (model: GridPaginationModel) => void;
  sortModel: GridSortModel;
  handleSortModelChange: (model: GridSortModel) => void;
  captureScrollAnchor: () => void;
  filterModel: GridFilterModel;
  setFilterModel: Dispatch<SetStateAction<GridFilterModel>>;
  handleFilterModelChange: (model: GridFilterModel) => void;
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: Dispatch<SetStateAction<GridColumnVisibilityModel>>;
  columnWidthModel: GridColumnWidthModel;
  handleColumnWidthChange: NonNullable<DataGridProps['onColumnWidthChange']>;
};
const DEFAULT_VIEWPORT_HEIGHT = 780;

const isColumnVisibilityModelEqual = (
  left: GridColumnVisibilityModel,
  right: GridColumnVisibilityModel,
): boolean => {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every((key) => left[key] === right[key]);
};

const useResolvedHeight = (autoHeight: boolean, height?: number): number | undefined => {
  const [viewportHeight, setViewportHeight] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_VIEWPORT_HEIGHT;
    return window.innerHeight;
  });
  useEffect(() => {
    if (autoHeight) return;
    if (typeof height === 'number') return;
    if (typeof window === 'undefined') return;
    const handleResize = (): void => {
      setViewportHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [autoHeight, height]);

  return useMemo(() => {
    if (autoHeight) return undefined;
    if (typeof height === 'number') return height;
    const buffer = 260;
    return Math.max(420, viewportHeight - buffer);
  }, [autoHeight, height, viewportHeight]);
};
const useGridStorageKey = ({
  persistKey,
  providedQueryKey,
  queryKeyPrefix,
  queryPath,
}: {
  persistKey?: string | null;
  providedQueryKey?: QueryKey;
  queryKeyPrefix?: QueryKey;
  queryPath: string;
}): string | null =>
  useMemo(
    () => computeStorageKey(persistKey, providedQueryKey, queryKeyPrefix, queryPath),
    [persistKey, providedQueryKey, queryKeyPrefix, queryPath],
  );

export const useServerDataGridState = <T extends GridValidRowModel>({
  queryPath,
  queryFn,
  initialData,
  columns,
  pageSizeOptions,
  initialPageSize,
  height,
  noRowsText = 'No results found',
  filteredNoRowsText,
  onRowsChange,
  onQueryMeta,
  onDataUpdatedAt,
  fixedFilters,
  search,
  initialSortModel: initialSortModelProp,
  initialFilterModel: initialFilterModelProp,
  queryKeyPrefix,
  queryKey: providedQueryKey,
  restoreFilterModel = true,
  onRefetch,
  onInvalidate,
  onQueryInput,
  isAuthenticated = true,
  organisationId,
  projectId,
  persistKey,
  syncStateToUrl = false,
  autoHeight = false,
  columnVisibilityModel: columnVisibilityModelProp,
  columnVisibilityPresets,
  columnVisibilityPreset,
  refetchIntervalMs,
  staleTimeMs,
  refetchOnWindowFocus,
  refetchOnMount,
  skeletonGetRowHeight,
  getRowHeight,
}: UseServerDataGridStateParams<T>): UseServerDataGridStateResult<T> => {
  const [searchParams, setSearchParams] = useSearchParams();
  const invalidateQueries = useInvalidateQueries();
  const urlState = useMemo(
    () => parseUrlState(searchParams, syncStateToUrl),
    [searchParams, syncStateToUrl],
  );

  const baseStorageKey = useGridStorageKey({
    persistKey,
    providedQueryKey,
    queryKeyPrefix,
    queryPath,
  });

  const resolvedHeight = useResolvedHeight(autoHeight, height);

  const persistedState = useMemo(() => getPersistedState(baseStorageKey), [baseStorageKey]);
  const {
    resolvedPageSizeOptions,
    resolvedInitialPageSize,
    initialPaginationModel,
    initialSortModel,
    initialFilterModel,
    initialColumnVisibilityModel,
    initialColumnWidthModel,
  } = useGridInitialState({
    columns,
    pageSizeOptions,
    initialPageSize,
    urlState,
    persistedState,
    initialSortModel: initialSortModelProp,
    initialFilterModel: initialFilterModelProp,
    restoreFilterModel,
    columnVisibilityModel: columnVisibilityModelProp,
    columnVisibilityPresets,
    columnVisibilityPreset,
  });

  const [paginationModel, setPaginationModel] =
    useState<GridPaginationModel>(initialPaginationModel);
  const { sortModel, handleSortModelChange: setSortModelInternal } =
    useGridSortState(initialSortModel);
  const [filterModel, setFilterModel] = useState<GridFilterModel>(initialFilterModel);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    initialColumnVisibilityModel,
  );
  const [columnWidthModel, setColumnWidthModel] =
    useState<GridColumnWidthModel>(initialColumnWidthModel);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const toErrorMessage = useApiErrorMessage();
  useEffect(() => {
    if (columnVisibilityModelProp === undefined) return;
    setColumnVisibilityModel((previousModel) =>
      isColumnVisibilityModelEqual(previousModel, columnVisibilityModelProp)
        ? previousModel
        : columnVisibilityModelProp,
    );
  }, [columnVisibilityModelProp]);

  useGridPersistence({
    baseStorageKey,
    organisationId,
    projectId,
    paginationModel,
    sortModel,
    filterModel,
    columnVisibilityModel,
    columnWidthModel,
  });

  const handleColumnWidthChange = useCallback<NonNullable<DataGridProps['onColumnWidthChange']>>(
    (params) => {
      const width = params.width;
      const field = params.colDef.field;
      if (typeof width !== 'number') return;
      setColumnWidthModel((prev) => {
        if (prev[field] === width) return prev;
        return { ...prev, [field]: width };
      });
    },
    [],
  );

  useEffect(() => {
    if (!resolvedPageSizeOptions.includes(paginationModel.pageSize)) {
      setPaginationModel((prev) => ({ ...prev, page: 0, pageSize: resolvedInitialPageSize }));
    }
  }, [paginationModel.pageSize, resolvedInitialPageSize, resolvedPageSizeOptions]);

  const { queryInput, queryInputKey, fixedFiltersKey } = useGridQueryInput({
    paginationModel,
    sortModel,
    filterModel,
    fixedFilters,
    search,
  });
  const finalQueryKey = useGridQueryKey({
    queryPath,
    queryKeyPrefix,
    providedQueryKey,
    organisationId,
    projectId,
    queryInputKey,
  });
  const [pendingPaginationModel, setPendingPaginationModel] = useState<GridPaginationModel | null>(
    null,
  );

  const {
    query,
    rowsData,
    rowCount,
    showInitialSkeleton,
    showFetchSkeleton,
    lastUpdatedAt,
    queryIsFetching,
    queryIsFetched,
  } = useGridQueryLifecycle<T>({
    finalQueryKey,
    isAuthenticated,
    queryFn,
    initialData,
    queryInput,
    preservePreviousData: pendingPaginationModel !== null,
    staleTimeMs,
    refetchOnWindowFocus,
    refetchOnMount,
    refetchIntervalMs,
  });

  useEffect(() => {
    if (queryIsFetching) return;
    const lastPage = Math.max(0, Math.ceil(rowCount / Math.max(1, paginationModel.pageSize)) - 1);
    if (paginationModel.page <= lastPage) return;
    const nextModel = { ...paginationModel, page: lastPage };
    setPendingPaginationModel(nextModel);
    setPaginationModel(nextModel);
  }, [paginationModel.page, paginationModel.pageSize, queryIsFetching, rowCount]);

  const {
    handlePaginationModelChange: handlePaginationModelChangeWithScrollRetention,
    handleSortModelChange,
    captureScrollAnchor,
  } = useGridScrollRetention({
    setPaginationModel,
    setSortModelInternal,
    paginationPage: paginationModel.page,
    queryIsFetching,
    queryDataUpdatedAt: query.dataUpdatedAt,
  });

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      const paginationChanged =
        model.page !== paginationModel.page || model.pageSize !== paginationModel.pageSize;
      setPendingPaginationModel(paginationChanged ? model : null);
      handlePaginationModelChangeWithScrollRetention(model);
    },
    [
      handlePaginationModelChangeWithScrollRetention,
      paginationModel.page,
      paginationModel.pageSize,
    ],
  );

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      captureScrollAnchor();
      setFilterModel(model);
    },
    [captureScrollAnchor],
  );

  const lastRetainedQueryInputKey = useRef<string | null>(null);
  useEffect(() => {
    if (!queryIsFetched) {
      lastRetainedQueryInputKey.current = queryInputKey;
      return;
    }
    if (lastRetainedQueryInputKey.current === null) {
      lastRetainedQueryInputKey.current = queryInputKey;
      return;
    }
    if (lastRetainedQueryInputKey.current === queryInputKey) return;
    lastRetainedQueryInputKey.current = queryInputKey;
    captureScrollAnchor();
  }, [captureScrollAnchor, queryInputKey, queryIsFetched]);

  const searchKey = useMemo(() => search ?? '', [search]);
  const lastFixedFiltersKey = useRef(fixedFiltersKey);
  useEffect(() => {
    if (lastFixedFiltersKey.current === fixedFiltersKey) return;
    lastFixedFiltersKey.current = fixedFiltersKey;
    if (paginationModel.page === 0) return;
    captureScrollAnchor();
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [captureScrollAnchor, fixedFiltersKey, paginationModel.page]);

  const lastSearchKey = useRef(searchKey);
  useEffect(() => {
    if (lastSearchKey.current === searchKey) return;
    lastSearchKey.current = searchKey;
    if (paginationModel.page === 0) return;
    captureScrollAnchor();
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [captureScrollAnchor, paginationModel.page, searchKey]);

  useGridCallbacks<T>({
    onQueryInput,
    queryInput,
    queryInputKey,
    onRowsChange,
    rowsData,
    onQueryMeta,
    queryData: query.data,
    onDataUpdatedAt,
    lastUpdatedAt,
    onRefetch,
    onInvalidate,
    invalidateQueries,
    finalQueryKey,
    queryRefetch: query.refetch,
  });

  useEffect(() => {
    if (query.status === 'error') {
      setErrorMessage(toErrorMessage(query.error));
    }
    if (query.status === 'success') {
      setErrorMessage(null);
    }
  }, [query.error, query.status, toErrorMessage]);

  useGridUrlSync({
    syncStateToUrl,
    searchParams,
    setSearchParams,
    paginationModel,
    sortModel,
    filterModel,
  });

  const hasActiveFilters = mapGridFilters(filterModel).length > 0;
  const resolvedNoRowsText = hasActiveFilters
    ? (filteredNoRowsText ?? 'No results match your filters')
    : noRowsText;
  const resolvedSkeletonRowHeight =
    skeletonGetRowHeight === null ? undefined : (skeletonGetRowHeight ?? getRowHeight);
  useEffect(() => {
    if (pendingPaginationModel === null) return;
    if (queryIsFetching) return;
    setPendingPaginationModel(null);
  }, [pendingPaginationModel, paginationModel, queryIsFetching]);
  const showPaginationSkeleton = pendingPaginationModel !== null;

  return {
    errorMessage,
    rowsData,
    rowCount,
    showInitialSkeleton,
    showFetchSkeleton,
    showPaginationSkeleton,
    lastUpdatedAt,
    queryIsFetching,
    queryIsFetched,
    resolvedPageSizeOptions,
    resolvedInitialPageSize,
    resolvedHeight,
    resolvedNoRowsText,
    resolvedSkeletonRowHeight,
    paginationModel,
    setPaginationModel,
    handlePaginationModelChange,
    sortModel,
    handleSortModelChange,
    captureScrollAnchor,
    filterModel,
    setFilterModel,
    handleFilterModelChange,
    columnVisibilityModel,
    setColumnVisibilityModel,
    columnWidthModel,
    handleColumnWidthChange,
  };
};
